/* ==========================================================================
   fk-ui.js — small interactive behaviours for the redesign.
   No framework. Delegated listeners so section re-renders in the theme editor
   never need re-binding.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Size ladder — mobile vertical tabs                                  */
  /* ------------------------------------------------------------------ */

  function openLadderTab(tab) {
    var host = tab.closest('[data-fk-ladder]');
    if (!host) return;

    var tabs = host.querySelectorAll('[data-fk-ladder-tab]');
    var panels = host.querySelectorAll('[data-fk-ladder-panel]');
    var isOpen = tab.getAttribute('aria-expanded') === 'true';

    tabs.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    panels.forEach(function (p) { p.hidden = true; });

    if (isOpen) return; /* tapping the open one closes it */

    tab.setAttribute('aria-expanded', 'true');
    var panel = document.getElementById(tab.getAttribute('aria-controls'));
    if (panel) panel.hidden = false;
  }

  /* ------------------------------------------------------------------ */
  /* Generic tab group (product page: Why preserved / Reviews / Questions)*/
  /* ------------------------------------------------------------------ */

  function selectTab(tab) {
    var host = tab.closest('[data-fk-tabs]');
    if (!host) return;

    host.querySelectorAll('[data-fk-tab]').forEach(function (t) {
      t.setAttribute('aria-selected', String(t === tab));
      t.setAttribute('tabindex', t === tab ? '0' : '-1');
    });

    host.querySelectorAll('[data-fk-tabpanel]').forEach(function (p) {
      p.hidden = p.id !== tab.getAttribute('aria-controls');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Filter panel                                                        */
  /* ------------------------------------------------------------------ */

  function toggleFilters(btn) {
    var host = btn.closest('[data-fk-filters]');
    if (!host) return;
    var panel = host.querySelector('[data-fk-filters-panel]');
    if (!panel) return;

    var open = !panel.hidden;
    panel.hidden = open;
    btn.setAttribute('aria-expanded', String(!open));
  }

  /* ------------------------------------------------------------------ */
  /* Ready-today badges — show only the badge for the selected store      */
  /* ------------------------------------------------------------------ */

  function renderReadyBadges() {
    var C = window.FK && window.FK.StoreContext;
    var key = C ? C.get() : 'none';

    document.querySelectorAll('.fk-ready-badge').forEach(function (el) {
      el.hidden = el.getAttribute('data-fk-ready-for') !== key;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky add-to-cart                                                  */
  /* ------------------------------------------------------------------ */

  function initStickyAtc() {
    var bar = document.querySelector('[data-fk-sticky-atc]');
    if (!bar) return;

    var anchor = document.querySelector('[data-fk-atc-anchor]');
    if (!anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        /* Show the bar once the real Add to cart button has scrolled away. */
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { rootMargin: '0px 0px -100% 0px', threshold: 0 });

    observer.observe(anchor);
  }

  /* ------------------------------------------------------------------ */
  /* Header — mobile drawer, mega menu, cart count                       */
  /* ------------------------------------------------------------------ */

  function openNav() {
    var drawer = document.querySelector('[data-fk-nav-drawer]');
    if (!drawer) return;
    drawer.hidden = false;
    document.documentElement.classList.add('fk-no-scroll');
    var btn = document.querySelector('[data-fk-nav-open]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    var focusable = drawer.querySelector('button, a');
    if (focusable) focusable.focus();

    /* aria-modal="true" promises the rest of the page is unreachable. It was
       not — tabbing past the last drawer link dropped focus into the page
       behind an opaque scrim, so a keyboard user ended up operating controls
       they could not see. inert removes them from focus AND from the
       accessibility tree, which is what the attribute already claims. */
    setInertBehindDrawer(true);
  }

  /* Everything that is NOT the drawer, marked inert while it is open. */
  function setInertBehindDrawer(on) {
    ['#MainContent', '.fk-header__inner', '.fk-footer'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (on) el.setAttribute('inert', '');
        else el.removeAttribute('inert');
      });
    });
  }

  function closeNav() {
    var drawer = document.querySelector('[data-fk-nav-drawer]');
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    setInertBehindDrawer(false);
    /* Do not unlock the page if the store picker is still open on top. */
    var picker = document.querySelector('[data-fk-picker]');
    if (!picker || picker.hidden) {
      document.documentElement.classList.remove('fk-no-scroll');
    }
    var btn = document.querySelector('[data-fk-nav-open]');
    if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.focus(); }
  }

  function closeAllMegas(except) {
    document.querySelectorAll('[data-fk-mega]').forEach(function (item) {
      if (item === except) return;
      item.classList.remove('is-open');
      var link = item.querySelector('.fk-nav__link');
      if (link) link.setAttribute('aria-expanded', 'false');
    });
  }

  function initMegaMenus() {
    document.querySelectorAll('[data-fk-mega]').forEach(function (item) {
      if (item.dataset.fkMegaBound) return;
      item.dataset.fkMegaBound = 'true';

      var link = item.querySelector('.fk-nav__link');
      var closeTimer = null;

      function openMega() {
        if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
        closeAllMegas(item);
        item.classList.add('is-open');
        if (link) link.setAttribute('aria-expanded', 'true');
      }

      function closeMega() {
        item.classList.remove('is-open');
        if (link) link.setAttribute('aria-expanded', 'false');
      }

      item.addEventListener('mouseenter', openMega);

      /* Close on a short delay rather than instantly. The CSS bridge handles a
         straight vertical move; this covers a diagonal one, where the pointer
         can clip the corner of the item on its way to a wide panel. Re-entering
         within the delay cancels it, so the menu feels sticky instead of
         snapping shut the moment the cursor wavers. */
      item.addEventListener('mouseleave', function () {
        if (closeTimer) clearTimeout(closeTimer);
        closeTimer = setTimeout(function () { closeMega(); closeTimer = null; }, 180);
      });

      /* Keyboard: the panel opens on focus and closes when focus leaves. */
      item.addEventListener('focusin', function () {
        closeAllMegas(item);
        item.classList.add('is-open');
        if (link) link.setAttribute('aria-expanded', 'true');
      });

      item.addEventListener('focusout', function (e) {
        if (item.contains(e.relatedTarget)) return;
        item.classList.remove('is-open');
        if (link) link.setAttribute('aria-expanded', 'false');
      });
    });
  }

  function updateCartCount(cart) {
    var badge = document.querySelector('[data-fk-cart-count]');
    if (!badge || !cart) return;
    var count = cart.item_count != null ? cart.item_count : 0;
    badge.textContent = count;
    badge.hidden = count === 0;
  }


  /* ------------------------------------------------------------------ */
  /* Variant switching (PDP)                                             */
  /* Was entirely missing: the selects rendered but nothing read them, so */
  /* the hidden id stayed on the first variant and the price never moved. */
  /* ------------------------------------------------------------------ */

  function variantMap() {
    var el = document.querySelector('[data-fk-variant-map]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function money(cents) {
    var n = (cents || 0) / 100;
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  /* ------------------------------------------------------------------ */
  /* Swapping the PDP's main photo — one path for both callers            */
  /* ------------------------------------------------------------------ */

  /* The main image is server-rendered responsive: `image_tag` emits a srcset
     of candidate widths plus a `sizes`. Both callers below used to throw that
     away and set a bare 1200px src, which meant the FIRST swap cost every
     phone the full-width file. syncVariant() runs in init(), so on a product
     whose first variant is not the product's own featured media that happened
     on plain page load, before any interaction.

     Capture the shape the server chose once — while the attributes are still
     there — and rebuild it from each new URL instead. Reading the widths off
     the rendered srcset rather than hardcoding them keeps this honest if the
     Liquid's `widths:` list ever changes. */
  var mainImgProfile = null;

  function mainImageProfile(img) {
    if (mainImgProfile) return mainImgProfile;
    var widths = [];
    (img.getAttribute('srcset') || '').split(',').forEach(function (part) {
      var m = part.trim().match(/\s(\d+)w$/);
      if (m) widths.push(parseInt(m[1], 10));
    });
    mainImgProfile = { widths: widths, sizes: img.getAttribute('sizes') || '' };
    return mainImgProfile;
  }

  function setMainImage(img, url, alt) {
    if (!img || !url) return;
    var p = mainImageProfile(img);

    /* Shopify CDN URLs take the size as a `width=` query param, so the whole
       candidate set is derivable from the one URL we were handed. */
    if (p.widths.length && /[?&]width=\d+/.test(url)) {
      img.setAttribute('srcset', p.widths.map(function (w) {
        return url.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
      }).join(', '));
      if (p.sizes) img.setAttribute('sizes', p.sizes);
    } else {
      /* No candidates to rebuild from. Clear BOTH — a srcset left in place
         would outrank the src we are about to set and the swap would silently
         do nothing, and a `sizes` with no srcset describes a box that no
         longer applies. */
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
    }

    img.src = url;
    if (alt) img.alt = alt;
  }

  function syncVariant() {
    var map = variantMap();
    var wrap = document.querySelector('[data-fk-variants]');
    if (!map || !wrap) return;

    var chosen = Array.prototype.map.call(
      wrap.querySelectorAll('[data-fk-option-index]'),
      function (sel) { return sel.value; }
    );

    var match = null;
    map.forEach(function (v) {
      if (match) return;
      if (v.options.length === chosen.length && v.options.every(function (o, i) { return o === chosen[i]; })) match = v;
    });
    if (!match) return;

    var input = document.querySelector('[data-fk-variant-input]');
    if (input) input.value = match.id;

    var price = document.querySelector('[data-fk-price]');
    if (price) {
      var onSale = match.compare && match.compare > match.price;
      /* Same rule as snippets/fk-sale-tag.liquid: whole-number saving off the
         compare-at price, no tag under 1%. */
      var pct = onSale ? Math.round((match.compare - match.price) * 100 / match.compare) : 0;
      price.innerHTML = onSale
        ? '<s>' + money(match.compare) + '</s> <span class="fk-price-sale">' + money(match.price) + '</span>' +
          (pct >= 1 ? ' <span class="fk-sale-tag fk-sale-tag--lg">' + pct + '% off</span>' : '')
        : money(match.price);
    }

    var btn = document.querySelector('[data-fk-add]');
    if (btn) {
      btn.disabled = !match.available;
      /* Read the wording off the element. It is a section setting now, so a
         hardcoded string here would silently overwrite the operator's label the
         moment anyone changed a variant. */
      btn.textContent = match.available
        ? (btn.getAttribute('data-fk-label-add') || 'Add to cart')
        : (btn.getAttribute('data-fk-label-soldout') || 'Sold out');
    }

    /* Keep the sticky bar honest too. The `&& match.available` guard meant a
       sold-out variant left the sticky button ENABLED showing the previous
       variant's price — and it is type=submit on the product form, so it would
       POST a sold-out variant to /cart/add. Mirror the main button exactly. */
    var sticky = document.querySelector('.fk-sticky-atc__btn');
    if (sticky) {
      sticky.disabled = !match.available;
      sticky.textContent = match.available
        ? (sticky.getAttribute('data-fk-label-add') || 'Add') + ' · ' + money(match.price)
        : (sticky.getAttribute('data-fk-label-soldout') || 'Sold out');
    }

    /* THE MAIN PHOTO. Picking a colour moved the id, the price and the buy
       button but left the gallery on `product.featured_media` — so a shopper
       choosing Peach was looking at the default photo while the cart got Peach.

       REMOVING srcset IS THE WHOLE TRICK. The main image is rendered with
       `widths: '400,600,800,1000,1200'`, so it carries a srcset, and a srcset
       OUTRANKS src: assigning .src on its own changes the attribute and the
       browser keeps painting whichever candidate it already picked. Drop
       srcset and sizes first, then set src. (The thumbnail path next to this
       one gets away with a narrower guard only because its thumbs happen to
       carry a srcset of their own.)

       Guarded on a real change so re-selecting the same colour does not
       re-trigger a network fetch, and on a non-empty URL so a variant with no
       photo leaves the current one up instead of blanking the gallery. */
    if (match.image) {
      var mediaHost = document.querySelector('[data-fk-main-media]');
      var mainImg = mediaHost ? mediaHost.querySelector('img') : null;
      if (mainImg && mainImg.getAttribute('src') !== match.image) {
        setMainImage(mainImg, match.image, match.alt);
      }
    }

    /* Keep the strip's selected state honest when this variant's photo IS one
       of the thumbnails. It often is not — thumb_limit caps the strip at 8 and
       Bloombox has 16 colours — in which case every thumb correctly reads
       aria-current="false" and none is marked. */
    if (match.mediaId) {
      document.querySelectorAll('[data-fk-media]').forEach(function (t) {
        t.setAttribute('aria-current', String(t.getAttribute('data-fk-media') === String(match.mediaId)));
      });
    }

    /* ANNOUNCE THE VARIANT. The availability line under Add to cart answers
       "can I collect this today", and that answer is per VARIANT — Shopify
       stocks each colour and size separately, and a shop can carry one and not
       another. Before this event existed the line was computed once from a
       product-level tag and never moved, so switching to a variant the local
       shop has never carried still read "Ready today in Austin".
       fk-availability.js listens for this. */
    document.dispatchEvent(new CustomEvent('fk:variant-changed', {
      detail: { variantId: String(match.id), available: match.available }
    }));
  }

  /* ------------------------------------------------------------------ */
  /* PDP gallery thumbnails — data-fk-media had no handler at all         */
  /* ------------------------------------------------------------------ */

  function swapMainImage(thumb) {
    var host = document.querySelector('[data-fk-main-media]');
    var main = host ? host.querySelector('img') : null;
    var src = thumb.querySelector('img');
    if (!main || !src) return;

    /* THE OLD GUARD READ THE WRONG ELEMENT.

       It was `if (src.srcset)` — the THUMBNAIL's srcset — deciding whether to
       clear the MAIN image's. The two are unrelated: whether a thumb happens
       to carry a srcset says nothing about whether the main image does, and it
       is the main image's that would win.

       A srcset OUTRANKS src. The main image always renders with one
       (`widths: '400,600,800,1000,1200'`), so any time that guard fell false
       the assignment would update the attribute while the browser kept
       painting the candidate it had already chosen — a swap that silently does
       nothing. It only ever worked because the thumbs are rendered through
       image_tag and happen to get a srcset of their own; nothing guaranteed
       that, and a thumb rendered without one would have broken the gallery.

       setMainImage owns this now, for both callers, and rebuilds the srcset
       rather than dropping it. */
    setMainImage(main, src.src.replace(/(\?|&)width=\d+/, '$1width=1200'), src.alt);

    document.querySelectorAll('[data-fk-media]').forEach(function (t) {
      t.setAttribute('aria-current', String(t === thumb));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Card "Add" — Dawn's <product-form> is only registered on the product */
  /* template, so these forms were inert everywhere else. Add via AJAX.   */
  /* ------------------------------------------------------------------ */

  function ajaxAdd(form) {
    var btn = form.querySelector('button[name="add"]');
    var label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    /* The card and PDP forms are deliberately NOT wrapped in Dawn's
       <product-form>, so Dawn's own add handler never runs — which means
       nothing here opened the cart drawer or refreshed it. The item went in and
       the badge ticked over, but the drawer stayed shut and its contents stayed
       stale, which reads as "the cart drawer is broken".

       Ask for the drawer's sections in the same request and hand the response to
       Dawn's CartDrawer.renderContents(), which swaps the markup AND calls
       open() itself. */
    var drawer = document.querySelector('cart-drawer');
    var useDrawer = !!(drawer && typeof drawer.renderContents === 'function'
                       && typeof drawer.getSectionsToRender === 'function');
    var body = new FormData(form);
    if (useDrawer) {
      body.append('sections', drawer.getSectionsToRender().map(function (x) { return x.id; }).join(','));
      body.append('sections_url', window.location.pathname);
    }

    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (added) {
        if (useDrawer && added && added.sections) {
          /* Nothing else clears is-empty from the ELEMENT — cart.js only does it
             on a cart update, not on an add — and the header cart link checks
             that class to decide whether to open or navigate. Adding to an empty
             cart would otherwise leave the icon still routing to /cart. */
          drawer.classList.remove('is-empty');
          drawer.renderContents(added);
        }
        return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        updateCartCount(cart);
        document.dispatchEvent(new CustomEvent('fk:cart-updated', { detail: cart }));
        if (btn) { btn.textContent = 'Added'; setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1400); }
      })
      .catch(function () {
        if (btn) { btn.textContent = label; btn.disabled = false; }
      });
  }


  /* ------------------------------------------------------------------ */
  /* Custom band carousel — a SINGLE STAGE with stacked slides.           */
  /* Replaces the previous scroll-rail logic: the prototype is one square  */
  /* stage that cross-fades, not a rail that scrolls.                      */
  /* ------------------------------------------------------------------ */

  /* Carousel state is PER BAND, not per document. It used to be one module-level
     index with document-wide queries, so two Custom band sections on one page
     would merge every slide into a single list: advancing one carousel
     cross-faded a slide belonging to the other and both counters showed the
     combined total. The index now lives on the band element itself. */
  function bandRoot(el) {
    return el && el.closest ? el.closest('.fk-band') : null;
  }

  function bandSlides(root) {
    return (root || document).querySelectorAll('[data-fk-band-slide]');
  }

  function showBandSlide(i, root) {
    root = root || document.querySelector('.fk-band');
    if (!root) return;

    var slides = bandSlides(root);
    if (!slides.length) return;

    /* Wrap around in both directions. */
    var idx = (i + slides.length) % slides.length;
    root.dataset.fkBandIndex = String(idx);

    slides.forEach(function (s, n) { s.classList.toggle('is-active', n === idx); });

    root.querySelectorAll('[data-fk-band-dot]').forEach(function (d, n) {
      d.classList.toggle('is-active', n === idx);
    });

    var counter = root.querySelector('[data-fk-band-counter]');
    if (counter) counter.textContent = (idx + 1) + ' / ' + slides.length;
  }

  function bandIndexOf(root) {
    return Number(root && root.dataset.fkBandIndex) || 0;
  }

  /* ------------------------------------------------------------------ */
  /* Delegated wiring                                                    */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    var ladderTab = e.target.closest('[data-fk-ladder-tab]');
    if (ladderTab) { openLadderTab(ladderTab); return; }

    var tab = e.target.closest('[data-fk-tab]');
    if (tab) { e.preventDefault(); selectTab(tab); return; }

    var filterBtn = e.target.closest('[data-fk-filters-toggle]');
    if (filterBtn) { e.preventDefault(); toggleFilters(filterBtn); return; }

    var prev = e.target.closest('[data-fk-band-prev]');
    if (prev) { var pr = bandRoot(prev); showBandSlide(bandIndexOf(pr) - 1, pr); return; }
    var next = e.target.closest('[data-fk-band-next]');
    if (next) { var nr = bandRoot(next); showBandSlide(bandIndexOf(nr) + 1, nr); return; }
    var dot = e.target.closest('[data-fk-band-dot]');
    if (dot) { showBandSlide(Number(dot.getAttribute('data-fk-band-dot')), bandRoot(dot)); return; }

    var thumb = e.target.closest('[data-fk-media]');
    if (thumb) { e.preventDefault(); swapMainImage(thumb); return; }

    if (e.target.closest('[data-fk-nav-open]')) { e.preventDefault(); openNav(); return; }
    if (e.target.closest('[data-fk-nav-close]')) { e.preventDefault(); closeNav(); return; }
  });

  /* Escape peels ONE layer. The store picker can sit on top of the open drawer
     (the drawer hosts its own store pill), and closing both at once dumped the
     shopper back to the page having lost their place.

     Reading picker.hidden in the bubble phase does NOT work: fk-store-ui.js has
     already closed it by then, so it always looks shut. Record the state in the
     CAPTURE phase, before any other listener has had a chance to mutate it. */
  var escapeHitPicker = false;

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var picker = document.querySelector('[data-fk-picker]');
    escapeHitPicker = !!(picker && !picker.hidden);
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (escapeHitPicker) { escapeHitPicker = false; return; }  /* the picker consumed it */

    closeNav();
    closeAllMegas();
  });

  document.addEventListener('fk:cart-updated', function (e) { updateCartCount(e.detail); });

  /* Dawn's own product form adds via product-form.js, which publishes through
     pubsub and re-renders only Dawn's cart nodes — our bubble never heard about
     it and went stale. Subscribe the same way fk-gwp.js does. */
  /* PUB_SUB_EVENTS is declared with `const` at the top level of a classic
     script, so it is a global BINDING but NOT a window property —
     window.PUB_SUB_EVENTS is undefined and this guard silently never ran.
     Reference the bare name instead. */
  if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(updateCartCount)
        .catch(function () { /* cart unavailable — leave the bubble as it is */ });
    });
  }

  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[data-fk-tab]');
    if (!tab) return;
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;

    var host = tab.closest('[data-fk-tabs]');
    if (!host) return;

    var tabs = Array.prototype.slice.call(host.querySelectorAll('[data-fk-tab]'));
    var i = tabs.indexOf(tab);
    var next = e.key === 'ArrowRight' ? tabs[i + 1] || tabs[0] : tabs[i - 1] || tabs[tabs.length - 1];

    e.preventDefault();
    selectTab(next);
    next.focus();
  });

  /* ------------------------------------------------------------------ */
  /* Filter + sort submission (native storefront filtering)              */
  /* ------------------------------------------------------------------ */

  function currentPath() {
    return window.location.pathname;
  }

  function submitFacets(form) {
    var params = new URLSearchParams();

    form.querySelectorAll('input[type="checkbox"]:checked').forEach(function (input) {
      params.append(input.name, input.value);
    });

    var sort = document.querySelector('[data-fk-sort]');
    if (sort && sort.value) params.set('sort_by', sort.value);

    var qs = params.toString();
    window.location.href = currentPath() + (qs ? '?' + qs : '');
  }

  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-fk-option-index]')) { syncVariant(); return; }

    /* Button and thumbnail option styles. The radio is what the shopper and the
       keyboard actually operate; the hidden input is the single per-group value
       carrier that syncVariant reads, so that function needs no knowledge of
       which style is on the page. */
    if (e.target.matches('[data-fk-option-radio]')) {
      var group = e.target.closest('.fk-variant-group');
      if (group) {
        var owner = e.target.getAttribute('data-fk-owner');
        var hidden = group.querySelector('input[type="hidden"][data-fk-option-index="' + owner + '"]');
        if (hidden) hidden.value = e.target.value;
        var chosen = group.querySelector('[data-fk-option-chosen="' + owner + '"]');
        if (chosen) chosen.textContent = e.target.value;
      }
      syncVariant();
      return;
    }

    if (e.target.matches('[data-fk-sort]')) {
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', e.target.value);
      window.location.href = currentPath() + '?' + params.toString();
    }
  });

  document.addEventListener('submit', function (e) {
    var card = e.target.closest('.fk-card__addform');
    if (card) { e.preventDefault(); ajaxAdd(card); return; }

    /* The PDP form was a native browser POST: full page reload to /cart, so the
       shopper never saw the drawer, the free-shipping meter or the gift picker
       at the moment the offer actually matters. Route it through the same AJAX
       path the product cards already use, then open the drawer. */
    var pdp = e.target.closest('.fk-pdp__form');
    if (pdp) {
      e.preventDefault();
      ajaxAdd(pdp);
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }

    var facetForm = e.target.closest('[data-fk-facet-form]');
    if (facetForm) {
      e.preventDefault();
      submitFacets(facetForm);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Ready Today — swap the shelf and the live status with the store      */
  /* ------------------------------------------------------------------ */

  function renderReadyToday() {
    var C = window.FK && window.FK.StoreContext;
    var shelves = document.querySelectorAll('[data-fk-ready-shelf]');
    if (!shelves.length || !C) return;

    var key = C.get();
    var prompt = document.querySelector('[data-fk-ready-prompt]');
    var statusBox = document.querySelector('[data-fk-ready-status]');
    var pill = document.querySelector('[data-fk-ready-pill]');
    var countdown = document.querySelector('[data-fk-ready-countdown]');

    shelves.forEach(function (el) {
      el.hidden = el.getAttribute('data-fk-ready-shelf') !== key;
    });

    if (prompt) prompt.hidden = key !== 'none';
    if (!statusBox) return;

    if (key === 'none') {
      statusBox.hidden = true;
      return;
    }

    var s = C.status(key);
    var store = C.getStore(key);
    statusBox.hidden = false;

    var closed = !s.known || s.state === 'closed' || s.state === 'after-close';
    statusBox.classList.toggle('fk-ready__status--closed', closed);

    if (pill) {
      if (s.state === 'open') {
        pill.className = 'fk-status fk-status--open';
        pill.innerHTML = '<span class="fk-dot fk-dot--open"></span>Open till ' + C.formatHour(s.closeHour);
      } else if (s.state === 'before-open') {
        pill.className = 'fk-status fk-status--later';
        pill.innerHTML = '<span class="fk-dot fk-dot--later"></span>Opens ' + C.formatHour(s.openHour) + ' today';
      } else {
        pill.className = 'fk-status fk-status--closed';
        pill.innerHTML = '<span class="fk-dot fk-dot--closed"></span>Closed';
      }
    }

    if (countdown) {
      countdown.classList.remove('is-urgent');

      if (s.pastCutoff || closed) {
        /* Plain language, not "today's cutoff passes". */
        countdown.textContent = s.nextOpenHour != null
          ? 'Ready ' + s.nextOpenDayName + ' from ' + C.formatHour(s.nextOpenHour) +
            (store ? ' at ' + (store.hood || store.city) : '')
          : 'Ready when the shop next opens';
      } else {
        countdown.textContent = s.countdown + ' left to order for today';
        if (s.urgent) countdown.classList.add('is-urgent');
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Search — takes over the header on desktop, the drawer on mobile      */
  /* ------------------------------------------------------------------ */

  /* Both surfaces work the same way: a scope name ('header' | 'drawer'),
     a host element that gets .is-searching, and an input to focus. Taking the
     surface over in place is what keeps the layout from shifting and stops a
     second X appearing next to the one already there. */
  function searchHost(scope) {
    return scope === 'drawer'
      ? document.querySelector('.fk-nav-drawer__panel')
      : document.querySelector('.fk-header');
  }

  function searchIsOpen(scope) {
    var host = searchHost(scope);
    return !!host && host.classList.contains('is-searching');
  }

  function setSearchOpen(scope, open) {
    var host = searchHost(scope);
    if (!host) return;

    host.classList.toggle('is-searching', open);

    var toggle = document.querySelector('[data-fk-search-toggle="' + scope + '"]');
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      /* Two panels hanging off the same bar at once reads as a mistake. */
      if (scope === 'header') closeAllMegas();
      var input = document.querySelector('[data-fk-search-input="' + scope + '"]');
      if (input) input.focus();
    } else if (toggle) {
      toggle.focus();
    }
  }

  function initSearch() {
    document.querySelectorAll('[data-fk-search-toggle]').forEach(function (btn) {
      if (btn.dataset.fkSearchBound) return;
      btn.dataset.fkSearchBound = 'true';
      var scope = btn.getAttribute('data-fk-search-toggle');
      btn.addEventListener('click', function () { setSearchOpen(scope, !searchIsOpen(scope)); });
    });

    document.querySelectorAll('[data-fk-search-close]').forEach(function (btn) {
      if (btn.dataset.fkSearchCloseBound) return;
      btn.dataset.fkSearchCloseBound = 'true';
      btn.addEventListener('click', function () {
        var host = btn.closest('.fk-nav-drawer__panel') ? 'drawer' : 'header';
        setSearchOpen(host, false);
      });
    });

    if (document.body.dataset.fkSearchGlobalBound) return;
    document.body.dataset.fkSearchGlobalBound = 'true';

    /* Escape closes search FIRST and stops there, so it does not also collapse
       the drawer underneath — one press, one thing. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (searchIsOpen('drawer')) { e.stopImmediatePropagation(); setSearchOpen('drawer', false); return; }
      if (searchIsOpen('header')) { e.stopImmediatePropagation(); setSearchOpen('header', false); }
    }, true);

    /* Clicking outside the desktop bar closes it. The drawer takeover is modal
       within the drawer, so it is left alone. */
    document.addEventListener('click', function (e) {
      if (!searchIsOpen('header')) return;
      if (e.target.closest('.fk-header')) return;
      setSearchOpen('header', false);
    });

    /* Closing the drawer resets its search, or it reopens mid-search. */
    var drawer = document.querySelector('[data-fk-nav-drawer]');
    if (drawer) {
      new MutationObserver(function () {
        if (drawer.hasAttribute('hidden') && searchIsOpen('drawer')) setSearchOpen('drawer', false);
      }).observe(drawer, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer opener                                                   */
  /* ------------------------------------------------------------------ */

  /* The FK header cart is a plain <a href="/cart"> with none of Dawn's
     markup, so nothing ever bound a drawer opener to it: clicking it left the
     page. The cart drawer exists and fk-gwp.js mounts the gift picker inside
     it, which meant the whole GWP strip was invisible to customers. Open the
     drawer in place when the theme is set to drawer mode, and fall through to
     the cart page otherwise.

     An EMPTY cart used to fall through here too, on the reasoning that Dawn
     hides .drawer__header while the cart is empty and the drawer would have
     had no way out. That has not been true since session 28 restored the
     header and its close button (see the note in cart-drawer.liquid), so the
     exception was costing a full page load on the most common tap on the site
     - and hiding the meters at the one moment "$75 to free shipping" is worth
     reading, since an empty cart is exactly when nobody has spent anything. */
  function initCartDrawer() {
    if (document.body.dataset.fkCartDrawerBound) return;
    document.body.dataset.fkCartDrawerBound = 'true';

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-fk-cart-link]');
      if (!link) return;

      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;                       /* no drawer - let it navigate */
      if (typeof drawer.open !== 'function') return;

      e.preventDefault();
      drawer.open(link);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Collection — "Load more" instead of page 1/2/3                       */
  /* ------------------------------------------------------------------ */

  /* The button is a real <a href="{next page}">, so this is a progressive
     enhancement: with JS off it navigates, and the numbered links below it stay
     crawlable. Here we fetch that same URL, lift the cards out of the response
     and append them. Filter and sort params ride along because they are already
     part of paginate.next.url. */
  function initLoadMore() {
    if (document.body.dataset.fkLoadMoreBound) return;
    document.body.dataset.fkLoadMoreBound = 'true';

    /* The numbered links exist for no-JS and crawlers; hide them once the
       button is live so there are not two competing controls. */
    var pages = document.querySelector('[data-fk-pagination-pages]');
    if (pages && document.querySelector('[data-fk-load-more]')) pages.hidden = true;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-fk-load-more]');
      if (!btn || btn.dataset.loading) return;

      var url = btn.getAttribute('href');
      if (!url) return;

      e.preventDefault();
      btn.dataset.loading = 'true';
      var label = btn.textContent;
      btn.textContent = 'Loading…';

      fetch(url, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var grid = document.querySelector('[data-fk-collection-grid]');
          var incoming = doc.querySelector('[data-fk-collection-grid]');
          if (!grid || !incoming) throw new Error('grid missing');

          var added = incoming.children.length;
          while (incoming.firstElementChild) grid.appendChild(incoming.firstElementChild);

          /* Point the button at the NEXT next page, or retire it. */
          var nextBtn = doc.querySelector('[data-fk-load-more]');
          if (nextBtn) {
            btn.setAttribute('href', nextBtn.getAttribute('href'));
          } else {
            btn.remove();
          }

          var count = document.querySelector('[data-fk-pagination-count]');
          var newCount = doc.querySelector('[data-fk-pagination-count]');
          if (count && newCount) count.textContent = newCount.textContent;

          var status = document.querySelector('[data-fk-pagination-status]');
          if (status) {
            status.textContent = added === 1
              ? '1 more product loaded.'
              : added + ' more products loaded.';
          }

          /* Newly appended cards need the store-dependent badges resolved. */
          renderReadyBadges();
        })
        .catch(function () {
          /* Fall back to plain navigation rather than stranding the shopper. */
          window.location.href = url;
        })
        .finally(function () {
          delete btn.dataset.loading;
          if (btn.isConnected) btn.textContent = label;
        });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Horizontal rails — the slide indicator                              */
  /* ------------------------------------------------------------------ */

  /* .fk-rail--bleed hides its native scrollbar, so nothing told the shopper
     the row moves. This drives the bar that replaces it: thumb WIDTH is the
     visible share of the row, thumb POSITION is how far along you are. */
  function initRails() {
    document.querySelectorAll('[data-fk-rail]').forEach(function (rail) {
      if (rail.dataset.fkRailBound) return;
      rail.dataset.fkRailBound = 'true';

      /* Scoped to the rail's own parent rather than the document: the markup
         allows more than one rail per page even though only Custom & Events
         ships one today. */
      var host = rail.parentElement;
      var wrap = host && host.querySelector('[data-fk-rail-progress]');
      var track = wrap && wrap.querySelector('.fk-rail__progress-track');
      var thumb = wrap && wrap.querySelector('[data-fk-rail-thumb]');
      if (!wrap || !track || !thumb) return;

      var raf = 0;

      function paint() {
        raf = 0;

        var visible = rail.clientWidth;
        var maxScroll = rail.scrollWidth - visible;

        /* Nothing to slide. Stay hidden rather than draw a full-width thumb,
           which would advertise a scroll that cannot happen — the one thing
           worse than no indicator. Re-checked on every resize, so widening
           the window past the content hides it again. */
        if (maxScroll <= 1) {
          wrap.hidden = true;
          return;
        }
        wrap.hidden = false;

        /* Measured AFTER unhiding — a hidden element reports clientWidth 0. */
        var trackW = track.clientWidth;
        if (!trackW) return;

        /* Floor the thumb so a very long row still leaves something grabbable
           to look at instead of a 2px speck. */
        var thumbW = Math.max(28, Math.round(trackW * (visible / rail.scrollWidth)));
        var travel = trackW - thumbW;
        var x = travel > 0 ? Math.round((rail.scrollLeft / maxScroll) * travel) : 0;

        thumb.style.width = thumbW + 'px';
        thumb.style.transform = 'translateX(' + x + 'px)';
      }

      /* Scroll fires far faster than the screen repaints; coalesce to one
         write per frame or this thrashes layout on a touch drag.

         CANCEL AND REISSUE rather than an "already queued" boolean. A boolean
         can only be cleared by paint() actually running, and rAF does NOT run
         while the page is hidden — so a scroll that happened in a background
         tab latched the flag on and every later scroll returned early. This
         shape cannot latch: there is always exactly one pending callback, and
         whenever the browser next paints it runs with current values. */
      function schedule() {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(paint);
      }

      rail.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);

      /* Returning to a backgrounded tab, or a bfcache restore, can land with a
         scroll position the paused rAF never drew. Resync on the way back in. */
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) schedule();
      });

      /* Catches what resize misses: images settling, fonts swapping, a block
         added in the theme editor — anything that changes the row's width
         without the viewport changing. */
      if (window.ResizeObserver) {
        new ResizeObserver(schedule).observe(rail);
      }

      paint();
    });
  }

  function init() {
    initRails();
    initSearch();
    initLoadMore();
    initCartDrawer();
    renderReadyBadges();
    renderReadyToday();
    initStickyAtc();
    initMegaMenus();
    syncVariant();
    document.querySelectorAll('.fk-band').forEach(function (band) {
      if (bandSlides(band).length) showBandSlide(0, band);
    });
  }

  document.addEventListener('fk:store-context-changed', function () {
    renderReadyBadges();
    renderReadyToday();
  });
  document.addEventListener('fk:tick', renderReadyToday);
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
